import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  emptyReader,
  hasLocalData,
  loadReader,
  mergeEngagement,
  readerMode,
  rowsToUpsert,
  saveReader,
  type Engagement,
  type EngagementRow,
} from "@/lib/blog/reader-store";
import { isAgeChipId, type AgeChipId } from "@/lib/blog/age-bands";

export const READER_KEY = ["reader-engagement"] as const;

export type ReaderData = {
  mode: "account" | "device";
  userId: string | null;
  age: AgeChipId | null;
  posts: Record<string, Engagement>;
};

const toMap = (rows: EngagementRow[]) =>
  Object.fromEntries(rows.map((r) => [r.post_id, { saved_at: r.saved_at, read_at: r.read_at }]));

async function fetchReader(): Promise<ReaderData> {
  const { data } = await supabase.auth.getSession();
  const session = data.session;
  const local = loadReader();
  if (readerMode(session) === "device" || !session) {
    return { mode: "device", userId: null, age: isAgeChipId(local.age) ? local.age : null, posts: local.posts };
  }
  const userId = session.user.id;
  const { data: rows, error } = await supabase
    .from("post_engagement")
    .select("post_id, saved_at, read_at")
    .eq("user_id", userId);
  if (error) throw error;
  let remote = (rows ?? []) as EngagementRow[];
  let age = session.user.user_metadata?.blog_age_band ?? null;

  // First visit after sign-in: fold the device copy into the account, once.
  if (hasLocalData(local) || (local.age && !age)) {
    const merged = mergeEngagement(local.posts, remote);
    const writes = rowsToUpsert(merged, remote).map((r) => ({ ...r, user_id: userId }));
    if (writes.length) {
      const { error: upErr } = await supabase.from("post_engagement").upsert(writes);
      if (upErr) throw upErr;
    }
    if (local.age && !age) {
      age = local.age;
      await supabase.auth.updateUser({ data: { blog_age_band: local.age } });
    }
    remote = merged;
    saveReader(emptyReader());
  }
  return { mode: "account", userId, age: isAgeChipId(age) ? age : null, posts: toMap(remote) };
}

export function useReader() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: READER_KEY, queryFn: fetchReader, staleTime: 60_000 });
  const reader: ReaderData = data ?? { mode: "device", userId: null, age: null, posts: {} };

  const patch = useCallback(
    async (postId: string, change: Partial<Engagement>) => {
      const prev = qc.getQueryData<ReaderData>(READER_KEY) ?? reader;
      const cur = prev.posts[postId] ?? { saved_at: null, read_at: null };
      const nextPosts = { ...prev.posts, [postId]: { ...cur, ...change } };
      qc.setQueryData<ReaderData>(READER_KEY, { ...prev, posts: nextPosts });
      if (prev.mode === "device") {
        const s = loadReader();
        saveReader({ ...s, posts: { ...s.posts, [postId]: { ...cur, ...change } } });
        return true;
      }
      const { error } = await supabase
        .from("post_engagement")
        .upsert({ user_id: prev.userId!, post_id: postId, ...change });
      if (error) {
        qc.setQueryData<ReaderData>(READER_KEY, prev);
        toast.error("ما انحفظ التغيير — منجرّب كمان مرة؟");
        return false;
      }
      return true;
    },
    [qc, reader],
  );

  const isSaved = (postId: string) => Boolean(reader.posts[postId]?.saved_at);
  const isRead = (postId: string) => Boolean(reader.posts[postId]?.read_at);

  const toggleSave = async (postId: string) => {
    const saving = !isSaved(postId);
    const ok = await patch(postId, { saved_at: saving ? new Date().toISOString() : null });
    if (!ok) return;
    if (!saving) toast("أُزيل من المحفوظات");
    else if (reader.mode === "account")
      toast("تم حفظ المقال في لوحتي", { action: { label: "فتح", onClick: () => window.location.assign("/portal") } });
    else toast("تم حفظ المقال على هذا الجهاز");
  };

  const markRead = (postId: string) => {
    if (isRead(postId)) return;
    void patch(postId, { read_at: new Date().toISOString() });
  };

  const setAge = async (age: AgeChipId | null) => {
    const prev = qc.getQueryData<ReaderData>(READER_KEY) ?? reader;
    qc.setQueryData<ReaderData>(READER_KEY, { ...prev, age });
    if (prev.mode === "device") saveReader({ ...loadReader(), age });
    else await supabase.auth.updateUser({ data: { blog_age_band: age } });
  };

  return { reader, isSaved, isRead, toggleSave, markRead, setAge };
}
